import { useState, forwardRef, useEffect } from 'react'
import * as Yup from 'yup'
import { Formik } from 'formik'
import {
  Grid,
  Card,
  Divider,
  TextField,
  CardContent,
  Typography,
  Snackbar,
  MenuItem,
  Alert as MuiAlert
} from '@mui/material'
import MainCard, {
  ActionButton,
  SnackBarHideDuration,
  SnackBarTransition
} from '../../../utils/ui_utils'
import { Helmet } from 'react-helmet-async'
import { useCreateHostTerminal } from '../../../modules/terminals/terminalOfflineHooks'
import { useFetchAppFlavour } from '../../../modules/appFlavours/appFlavourHooks'

export function HostTerminalOfflineCreate() {
  const [snackbar, setSnackBar] = useState(false)
  const [PopUpMessage, setPopUpMessage] = useState(null)
  const [severity, setSeverity] = useState(null)
  const {
    createHostTerminal,
    createHostTerminalData,
    createHostTerminalError,
    createHostTerminalLoading
  } = useCreateHostTerminal()
  const [initialValues, setInitialValues] = useState({
    business_code: '',
    app_flavor_id: ''
  })

  const handleSnackbarClose = () => {
    setSnackBar(false)
  }
  const toastMessage = (message, severity) => {
    setPopUpMessage(message)
    setSeverity(severity)
    setSnackBar(true)
  }

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
  })

  const validationSchema = Yup.object().shape({
    business_code: Yup.string()
      .required('Required')
      .min(10, 'Min length is 2 character'),
    app_flavor_id: Yup.string().required('Required')
  })

  const handleSubmit = async (
    values,
    { resetForm, setErrors, setStatus, setSubmitting }
  ) => {
    await createHostTerminal(values, (success, error, message) => {
      if (success) {
        setInitialValues({ business_code: '', app_flavor_id: '' })
        toastMessage(message, 'success')
      }
      else {
        toastMessage(message, 'error')
      }
    })
  }
  const {
    getAppsForTerminalLoading,
    getAppsForTerminalData,
    getAppsForTerminalError,
    getAppsForTerminal
  } = useFetchAppFlavour()

  useEffect(() => {
    getAppsForTerminal()
  }, [])

  function SelectApp({ values, apps, touched, onBlur, onChange, onErrors }) {
    return (
      <>
        <TextField
          size="small"
          id="app_select"
          name="app_flavor_id"
          label="Application"
          value={values?.app_flavor_id}
          error={Boolean(touched.app_flavor_id && onErrors.app_flavor_id)}
          fullWidth
          onBlur={onBlur}
          margin="dense"
          onChange={onChange}
          variant="outlined"
          select
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {apps &&
            apps?.map((app) => (
              <MenuItem key={app?._id} value={app?._id}>
                {app?.flavor_name}
              </MenuItem>
            ))}
        </TextField>
      </>
    )
  }

  return (
    <>
      <Helmet title="Terminals" />
      <Grid
        container
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Grid item md={6}>
          <Typography variant="h4" gutterBottom display="inline">
            Create Terminal
          </Typography>
        </Grid>
      </Grid>

      <Divider sx={{ marginTop: 5 }} />

      <Grid container spacing={10} sx={{ paddingTop: 5 }}>
        <Grid item>
          <MainCard>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                isSubmitting,
                touched,
                values,
                status
              }) => (
                <form onSubmit={handleSubmit}>
                  <Card>
                    <CardContent>
                      <Grid container>
                        <Grid item>
                          <TextField
                            size="small"
                            name="business_code"
                            label="Business Code"
                            value={values.business_code}
                            error={Boolean(
                              touched.business_code && errors.business_code
                            )}
                            fullWidth
                            helperText={
                              touched.business_code && errors.business_code
                            }
                            onBlur={handleBlur}
                            margin="dense"
                            onChange={handleChange}
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                      </Grid>
                      <Grid container>
                        <SelectApp
                          values={values}
                          apps={getAppsForTerminalData}
                          touched={touched}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          onErrors={errors}
                        />
                      </Grid>
                    </CardContent>
                  </Card>
                  <Grid container justifyContent="flex-end">
                    <Grid item mr={5} mb={5}>
                      <ActionButton
                        variant="outlined"
                        size="medium"
                        type="submit"
                        mt={8}
                      >
                        Create
                      </ActionButton>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          </MainCard>
        </Grid>
      </Grid>
      <div>
        <Snackbar
          open={snackbar}
          autoHideDuration={SnackBarHideDuration}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          onClose={handleSnackbarClose}
          TransitionComponent={SnackBarTransition}
        >
          <Alert onClose={handleSnackbarClose} severity={severity}>
            {PopUpMessage}
          </Alert>
        </Snackbar>
      </div>
    </>
  )
}
